/* ========================================================================= *
 * THE TRADE DESK — the pure half. No React, no fetch.
 *
 * Brian, 2026-10-16: *"We need to have KPI's here. we need to know we've got
 * full real coverage with a plan."*
 *
 * ---- THE TWO COVERAGE NUMBERS, AND WHY THE LOWER ONE IS THE POINT -------
 *
 * Assigned is not covered. A championship is covered when it has a named owner
 * AND evidence it is actually being worked. Counted as "has an owner" the number
 * reads high and flatters; counted as "owner plus evidence of work" it reads far
 * lower and is true. So both are computed, both are labelled, and THE GAP
 * BETWEEN THEM is the headline — because the gap is the work.
 *
 * ⚠️ THE DEFINITION OF A PLAN IS NOT DECLARED HERE AND MUST NOT BE.
 * `src/lib/coverage.js` already defines the funnel, and it already MEASURED it
 * live over 5,000 records. Brian asked for the same signals on bowl
 * eligibility — *"reach out, artwork, etc."* — so this module READS that
 * module's `STAGES` rather than writing a second list. Two surfaces with two
 * definitions of "covered" is the defect, not the fix.
 *
 * ⚠️ AND coverage.js IS BLUNT ABOUT WHY MOST OF IT READS ZERO: THE FIELDS DO
 * NOT EXIST. Outreach, calls scheduled, calls held, proposals, delivery,
 * payment and reviews leave NO TRACE on the record — the event happens in the
 * real world and nothing changes. Its header says so, with the live
 * measurement attached.
 *
 * That shapes this whole module. A stage reports `measurable` next to its count,
 * and an unmeasurable stage renders as "there is nowhere to record this" rather
 * than as a zero. A zero because nobody did the work and a zero because there is
 * no field look identical on a dashboard and need completely opposite responses
 * — one is a management problem, the other is a schema problem. Collapsing them
 * would make this exactly the flattering KPI Brian said is worse than none.
 *
 * So the honest headline is NOT "x% covered". It is three numbers and a caveat:
 * owned, owned-with-a-plan, the gap, and the fact that the plan number can only
 * ever count the signals that have somewhere to live.
 * ========================================================================= */

import { STAGES } from './coverage.js'

const S = (v) => String(v == null ? '' : v).trim()

/** The owner stage, by id, so a rename in coverage.js breaks loudly here. */
const OWNED_ID = 'owned'

/**
 * THE PLAN STAGES: every stage AFTER ownership. Derived from coverage.js by
 * position rather than by a hand-typed list of ids, so a stage added there
 * appears here with no second edit.
 *
 * ⚠️ `owned` ITSELF IS NOT A PLAN SIGNAL. Having an owner is the thing the plan
 * number is being contrasted WITH; counting it as evidence of work would make
 * the two numbers identical and the gap always zero, which is precisely the
 * flattering answer.
 */
export const PLAN_STAGES = STAGES.filter((s) => s.id !== OWNED_ID)

/** The plan stages that can actually move. The rest are a schema gap. */
export const MEASURABLE_PLAN_STAGES = PLAN_STAGES.filter((s) => s.exists)
export const BLIND_PLAN_STAGES = PLAN_STAGES.filter((s) => !s.exists)

/**
 * Which plan signals are present on one record.
 *
 * ⚠️ RETURNS THE SIGNALS, NOT A BOOLEAN. "Has a plan" needs a threshold, and
 * the threshold is a setting Brian has not chosen — so the caller applies it
 * and this returns the evidence.
 */
export function planSignalsOf(rec) {
  const raw = rec?.raw || rec || {}
  return MEASURABLE_PLAN_STAGES.filter((s) => {
    try { return s.test(raw) === true } catch { return false }
  }).map((s) => s.id)
}

/**
 * THE TWO NUMBERS, AND THE GAP.
 *
 * @param records       the championship feed — the SAME feed the board draws
 * @param signalsNeeded how many plan signals count as "has a plan"
 *                      (RULES.kpis.planSignalsRequired, Brian's to set)
 */
export function coverageTwoWays(records = [], { signalsNeeded = 1 } = {}) {
  const total = records.length
  let owned = 0
  let ownedWithPlan = 0
  const perSignal = new Map(MEASURABLE_PLAN_STAGES.map((s) => [s.id, 0]))

  for (const r of records) {
    const raw = r?.raw || r || {}
    const hasOwner = Boolean(S(raw.sr_assigned_rep) || S(raw.sr_lead_rep))
    if (!hasOwner) continue
    owned += 1
    const sig = planSignalsOf(raw)
    for (const id of sig) perSignal.set(id, (perSignal.get(id) || 0) + 1)
    if (sig.length >= signalsNeeded) ownedWithPlan += 1
  }

  const pct = (n) => (total ? Math.round((n / total) * 1000) / 10 : null)

  return {
    total,
    /* ⚠️ `total === 0` GIVES `pct: null`, NEVER 0. "Nothing is covered" and
     * "there is nothing to cover" are different answers and must not render
     * the same. */
    /* ⚠️ THE LABELS SAY "ASSIGNED", NOT "OWNED", and that is a house rule with
     * a gate behind it (check-ownership-language.mjs): a rep is ASSIGNED a
     * record for a period, they do not own it and they hold no territory. The
     * HubSpot property is still `sr_assigned_rep` and is not renamed to suit a
     * label. */
    owned: { n: owned, pct: pct(owned), of: total,
      counts: 'championships with a named rep in sr_assigned_rep or sr_lead_rep',
      label: 'Assigned to a rep',
      reading: 'This is the FLATTERING number. An assignment is accountability, not work.' },
    ownedWithPlan: { n: ownedWithPlan, pct: pct(ownedWithPlan), of: total,
      signalsNeeded,
      counts: `championships assigned AND carrying at least ${signalsNeeded} recordable plan signal`,
      label: 'Assigned, with a plan',
      reading: 'This is the TRUE number, and it is the one to drive from.' },
    /* THE HEADLINE. */
    gap: { n: Math.max(0, owned - ownedWithPlan), pct: pct(Math.max(0, owned - ownedWithPlan)),
      of: owned,
      label: 'Assigned, no plan',
      counts: 'championships somebody is accountable for where nothing recordable has been done',
      reading: 'The gap between the two numbers above. This is the work.' },
    unowned: { n: total - owned, pct: pct(total - owned), of: total,
      label: 'Not assigned to anyone' },
    /* ---- EVERY SIGNAL, WITH WHETHER IT CAN EVEN MOVE --------------- */
    signals: PLAN_STAGES.map((s) => ({
      id: s.id,
      label: s.label,
      what: s.what,
      system: s.system,
      field: s.field,
      measurable: s.exists,
      n: s.exists ? (perSignal.get(s.id) || 0) : null,
      of: owned,
      /* ⚠️ THE SENTENCE IS THE FEATURE. A reader must not be able to mistake
       * "nobody did this" for "there is nowhere to write it down". */
      reading: !s.exists
        ? `Not measurable — nothing in ${s.system} records this, so this count can never move. `
          + 'That is a schema gap, not a performance one.'
        : (perSignal.get(s.id) || 0) === 0
          ? `Measurable and currently zero — \`${s.field}\` exists and nothing has been written `
            + 'to it. That IS a performance number.'
          : 'Measured.',
    })),
    /* ---- AND THE CEILING ON THE HONEST NUMBER, SAID OUT LOUD ------ */
    ceiling: {
      measurableSignals: MEASURABLE_PLAN_STAGES.length,
      blindSignals: BLIND_PLAN_STAGES.length,
      blindLabels: BLIND_PLAN_STAGES.map((s) => s.label),
      why: MEASURABLE_PLAN_STAGES.length === 0
        ? 'NO plan signal is recordable on a championship record today, so "owner and a plan" '
          + 'cannot rise above zero no matter how much work is done. The number is not wrong; '
          + 'there is nothing for it to read.'
        : `Only ${MEASURABLE_PLAN_STAGES.length} of ${PLAN_STAGES.length} plan signals are `
          + `recordable today (${MEASURABLE_PLAN_STAGES.map((s) => s.label).join(', ')}). The `
          + `other ${BLIND_PLAN_STAGES.length} — ${BLIND_PLAN_STAGES.map((s) => s.label).join(', ')} `
          + '— happen in the real world and leave no trace on the record, so this number is a '
          + 'FLOOR on the work being done, not a measure of it. See REQUIRED_PROPERTIES in '
          + 'coverage.js for the properties that would close it.',
    },
  }
}

/* ======================================================================== *
 * CARD HELPERS.
 * ======================================================================== */

/** "in 4 days" / "today" / "3 days ago". Never a bare number. */
export function daysWord(days) {
  if (days == null || !Number.isFinite(days)) return 'date unknown'
  if (days === 0) return 'today'
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
  return `in ${days} day${days === 1 ? '' : 's'}`
}

/** The tone a window state renders in. Muted on purpose — see DESIGN.md. */
export const WINDOW_TONE = Object.freeze({
  open: 'ok',
  closing_soon: 'warn',
  closed_at_event: 'danger',
  unknown: 'muted',
})

/**
 * THE FOUR SIZES, NAMED ON THIS SIDE TOO.
 *
 * ⚠️ NOT PURELY A MIRROR OF THE SERVER'S `LEVEL_LABEL`, AND THAT IS DELIBERATE.
 * The filter and the card both need these words; taking them only from the API
 * means a response that omits one renders a raw enum (`body-state-sport`) at a
 * rep, and it also means the surface cannot be asserted by
 * check-shipped-features.mjs, which reads the shipped BUNDLE and cannot see a
 * string that only ever arrives over the wire. The server's label wins when it
 * is there; this is the floor.
 */
export const SIZE_LABEL = Object.freeze({
  body: 'Whole governing body',
  'body-state': 'Body in one state',
  'body-state-sport': 'Body, state and sport',
  record: 'One championship',
})

export const sizeLabel = (level, serverLabel = '') =>
  S(serverLabel) || SIZE_LABEL[S(level)] || S(level) || 'Size unknown'

/* ---- THE TWO SENTENCES A REP MUST READ BEFORE THEY ACCEPT ------------- *
 * ⚠️ THE HEADING IS OURS, THE DETAIL IS THE SERVER'S. The server computes
 * WHICH records moved and by how much; the surface is responsible for making
 * sure the warning is readable as a warning even if that sentence is missing.
 * Written as a bare `{o.sizeChangeNote}` the whole protection disappeared the
 * moment the field was absent — and the absence would have looked like "no
 * change". */
export const SIZE_CHANGED_HEADING =
  'This offer has changed size since it was posted'
export const SIZE_CHANGED_FALLBACK =
  'The number of records in this scope has moved since the offering rep put it up. The detail '
  + 'could not be read, so claim it only if you are content to take what it covers NOW.'

export const SHORTFALL_HEADING =
  'This offer stops short of the whole scope'
export const SHORTFALL_FALLBACK =
  'The offering rep does not hold every record in this scope, so the offer covers only theirs. '
  + 'A rep cannot trade away another rep\'s assignment.'

export const WINDOW_LABEL = Object.freeze({
  open: 'Open',
  closing_soon: 'Closing soon',
  closed_at_event: 'Closed — executive only',
  unknown: 'Event date unknown',
})

/**
 * Apply the board filters. Pure, so the filter logic is testable without a DOM.
 *
 * ⚠️ AN EMPTY FILTER VALUE MEANS "EVERYTHING", not "nothing". A filter that
 * silently matched zero when nothing was selected would read as an empty board.
 */
export function filterOffers(offers = [], f = {}) {
  const want = (key) => S(f[key])
  return offers.filter((o) => {
    if (want('body') && S(o.body) !== want('body')) return false
    if (want('state') && S(o.state) !== want('state')) return false
    if (want('sport') && S(o.sport) !== want('sport')) return false
    if (want('size') && S(o.scopeLevel) !== want('size')) return false
    if (want('offeredBy') && S(o.offeredBy) !== want('offeredBy')) return false
    return true
  })
}

/**
 * The sort a rep actually wants: soonest event first, because the window is
 * hard and an offer that closes this week is the one to look at.
 *
 * ⚠️ AN UNKNOWN DATE SORTS LAST, NOT FIRST. Treating "no date" as "very soon"
 * would push the records nobody can date to the top of a board about deadlines.
 */
export function sortOffers(offers = []) {
  return [...offers].sort((a, b) => {
    const da = a?.window?.daysToEvent
    const db = b?.window?.daysToEvent
    const ka = Number.isFinite(da) ? da : Number.POSITIVE_INFINITY
    const kb = Number.isFinite(db) ? db : Number.POSITIVE_INFINITY
    if (ka !== kb) return ka - kb
    return String(a.postedAt || '').localeCompare(String(b.postedAt || ''))
  })
}

/** The settings whose status is 'proposed', for the panel that lists them. */
export function proposedOnly(rules = {}) {
  const out = []
  for (const [group, body] of Object.entries(rules || {})) {
    if (!body || typeof body !== 'object') continue
    for (const [k, v] of Object.entries(body)) {
      if (!k.endsWith('Status') || v !== 'proposed') continue
      const key = k.replace(/Status$/, '')
      out.push({ group, key, path: `${group}.${key}`, value: body[key] })
    }
  }
  return out
}

export default { PLAN_STAGES, MEASURABLE_PLAN_STAGES, BLIND_PLAN_STAGES, planSignalsOf,
  coverageTwoWays, daysWord, WINDOW_TONE, WINDOW_LABEL, SIZE_LABEL, sizeLabel,
  SIZE_CHANGED_HEADING, SIZE_CHANGED_FALLBACK, SHORTFALL_HEADING, SHORTFALL_FALLBACK,
  filterOffers, sortOffers, proposedOnly }
